import { Banner, Input, Select, Typography } from '@oresundsbron/bridge-ui';
import {
  useForm,
  useFormParams,
  useStepForm,
  useSubscribe,
} from '@oresundsbron/use-form';
import {
  businessCommuterContactInformationParser,
  businessContactInformationParser,
} from '@oresundsbron/validators';
import { FC, ReactNode, useEffect, useState } from 'react';
import { z } from 'zod';
import { FormButton } from '../Buttons';
import { FieldSet } from '../Fieldset';
import { displayOnlyPhoneNumber } from '~/lib/phone-code';
import { useFlags } from '~/components/Flags';

export type Data = z.input<typeof businessContactInformationParser> &
  Pick<z.input<typeof businessCommuterContactInformationParser>, 'commuter'>;

export type BusinessCustomerData = z.infer<
  typeof businessContactInformationParser
>;

type Labels = Record<keyof Omit<Data, 'commuter' | 'contact'>, string> & {
  contact: Record<keyof Data['contact'], string>;
} & {
  commuter: Record<keyof Data['commuter'], string>;
} & {
  loading: string;
  contactPersonLabel: string;
  commuterLabel: string;
  startDateDescription: string;
};

export const BusinessContactInformationCommonFields: FC<
  {
    labels: Omit<
      Labels,
      | 'loading'
      | 'commuterLabel'
      | 'commuter'
      | 'bypassVatno'
      | 'startDateDescription'
    >;
    countryOptions: { key: string; value: string }[];
    phoneCodeOptions: { key: string; value: string }[];
    placeholders: {
      country: string;
      phoneCode: string;
    };
    disableNameChange?: boolean;
  } & useFormParams<BusinessCustomerData>
> = ({
  namespace,
  defaultValues,
  labels,
  countryOptions,
  phoneCodeOptions,
  placeholders,
  disableNameChange,
}) => {
  const { registerField, getError, isActive } = useForm<BusinessCustomerData>({
    namespace,
    defaultValues,
  });

  const { getData } = useStepForm();

  const [country] = useSubscribe<BusinessCustomerData>(namespace, 'country');

  const [emailVerified, setEmailVerified] = useState<boolean>(false);

  useEffect(() => {
    const formData = getData() as {
      codeVerification: { codeVerified: string };
    };
    if (
      formData.codeVerification?.codeVerified &&
      formData.codeVerification?.codeVerified === 'true'
    ) {
      setEmailVerified(true);
    }
  }, [getData]);

  return (
    <>
      <Input
        className="col-span-full"
        {...registerField('companyName')}
        label={labels.companyName}
        error={getError('companyName')}
        disabled={disableNameChange || !isActive}
        required
      />
      <Select
        className="col-span-full sm:col-span-4 md:col-span-6"
        {...registerField('country')}
        label={labels.country}
        options={countryOptions.map((c) => ({ ...c, label: c.key }))}
        placeholder={placeholders.country}
        error={getError('country')}
        disabled={!isActive}
        required
      />
      <Input
        className="col-span-full sm:col-span-4 sm:col-start-5 md:col-span-6 md:col-start-7"
        {...registerField('vatno')}
        label={labels.vatno}
        error={getError('vatno')}
        disabled={disableNameChange || !isActive}
        required
      />
      {country === 'DK' ? (
        <Input
          className="col-span-full"
          {...registerField('ean')}
          label={labels.ean}
          error={getError('ean')}
          disabled={!isActive}
        />
      ) : null}
      <Input
        className="col-span-full sm:col-span-4 md:col-span-6"
        {...registerField('zipCode')}
        label={labels.zipCode}
        error={getError('zipCode')}
        disabled={!isActive}
        required
      />
      <Input
        className="col-span-full sm:col-span-4 sm:col-start-5 md:col-span-6 md:col-start-7"
        {...registerField('city')}
        label={labels.city}
        error={getError('city')}
        disabled={!isActive}
        required
      />
      <Input
        className="col-span-full"
        {...registerField('address')}
        label={labels.address}
        error={getError('address')}
        disabled={!isActive}
        required
      />
      <Select
        className="col-span-full sm:col-span-4 md:col-span-6"
        {...registerField('phoneCode')}
        label={labels.phoneCode}
        options={phoneCodeOptions.map((c) => ({
          value: displayOnlyPhoneNumber(c.key),
          label: c.key,
          key: c.key,
        }))}
        placeholder={placeholders.phoneCode}
        error={getError('phoneCode')}
        disabled={!isActive}
        required
      />
      <Input
        className="col-span-full sm:col-span-4 sm:col-start-5 md:col-span-6 md:col-start-7"
        {...registerField('phone')}
        label={labels.phone}
        error={getError('phone')}
        disabled={!isActive}
        required
      />

      <Typography className="col-span-full mt-16">
        {labels.contactPersonLabel}
      </Typography>
      <Input
        className="col-span-full sm:col-span-4 md:col-span-6"
        {...registerField('contact.firstName')}
        label={labels.contact.firstName}
        error={getError('contact.firstName')}
        disabled={!isActive}
        required
      />
      <Input
        className="col-span-full sm:col-span-4 sm:col-start-5 md:col-span-6 md:col-start-7"
        {...registerField('contact.lastName')}
        label={labels.contact.lastName}
        error={getError('contact.lastName')}
        disabled={!isActive}
        required
      />
      <Input
        className="col-span-full"
        {...registerField('contact.email')}
        label={labels.contact.email}
        error={getError('contact.email')}
        disabled={!isActive || emailVerified}
        required
      />
      <Select
        className="col-span-full sm:col-span-4 md:col-span-6"
        {...registerField('contact.phoneCode')}
        label={labels.contact.phoneCode}
        options={phoneCodeOptions.map((c) => ({
          value: displayOnlyPhoneNumber(c.key),
          label: c.key,
          key: c.key,
        }))}
        placeholder={placeholders.phoneCode}
        error={getError('contact.phoneCode')}
        disabled={!isActive}
        required
      />
      <Input
        className="col-span-full sm:col-span-4 sm:col-start-5 md:col-span-6 md:col-start-7"
        {...registerField('contact.phone')}
        label={labels.contact.phone}
        error={getError('contact.phone')}
        disabled={!isActive}
        required
      />
    </>
  );
};

export const BusinessContactInformation: FC<
  {
    labels: Labels;
    title: string;
    countryOptions: { key: string; value: string }[];
    phoneCodeOptions: { key: string; value: string }[];
    placeholders: {
      country: string;
      phoneCode: string;
    };
    commuter?: boolean;
    vatnoExist?: boolean;
    disclaimers: {
      vatno: ReactNode;
      vatnoCommuter: ReactNode;
    };
  } & useFormParams<Data>
> = ({
  labels,
  namespace,
  title,
  defaultValues,
  countryOptions = [],
  phoneCodeOptions = [],
  onValidate,
  placeholders,
  commuter,
  vatnoExist,
  disclaimers,
}) => {
  const {
    registerField,
    getError,
    validate,
    isValid,
    isActive,
    stepNr,
    resetFieldError,
  } = useForm<Data>({
    namespace,
    title,
    defaultValues,
    onValidate,
  });

  const [bypassVatno] = useSubscribe<Data>(namespace, 'bypassVatno');

  useEffect(() => {
    if (bypassVatno === 'true') {
      resetFieldError('vatno');
    }
  }, [bypassVatno, resetFieldError]);

  const flags = useFlags();
  const flagShowPendlareStartDate = !!flags?.get('showPendlareStartDate');

  return (
    <FieldSet nr={stepNr} title={title} id={namespace}>
      <BusinessContactInformationCommonFields
        namespace={namespace}
        labels={labels}
        countryOptions={countryOptions}
        phoneCodeOptions={phoneCodeOptions}
        placeholders={placeholders}
      />

      {commuter ? (
        <>
          <Typography className="col-span-full mt-16">
            {labels.commuterLabel}
          </Typography>
          <Input
            className="col-span-full sm:col-span-4 md:col-span-6"
            {...registerField('commuter.firstName')}
            label={labels.commuter.firstName}
            error={getError('commuter.firstName')}
            disabled={!isActive}
            required
          />
          <Input
            className="col-span-full sm:col-span-4 sm:col-start-5 md:col-span-6 md:col-start-7"
            {...registerField('commuter.lastName')}
            label={labels.commuter.lastName}
            error={getError('commuter.lastName')}
            disabled={!isActive}
            required
          />
          <Input
            className="col-span-full sm:col-span-4 md:col-span-6"
            {...registerField('commuter.email')}
            label={labels.commuter.email}
            error={getError('commuter.email')}
            disabled={!isActive}
            required
          />
          {flagShowPendlareStartDate ? (
            <>
              <Input
                className="col-span-full sm:col-span-4 md:col-span-6"
                {...registerField('commuter.startDate', { type: 'date' })}
                label={labels.commuter.startDate}
                error={getError('commuter.startDate')}
                disabled={!isActive}
                required
              />
              <p className="col-span-full mt-[-8px] text-sm sm:col-span-4 sm:col-start-5 md:col-span-6 md:col-start-7">
                {labels.startDateDescription}
              </p>
            </>
          ) : null}
        </>
      ) : null}

      {vatnoExist && !commuter ? (
        <>
          <Banner className="col-span-full">{disclaimers.vatno}</Banner>
          <Input
            className="col-span-full"
            {...registerField('bypassVatno', {
              type: 'checkbox',
              value: 'true',
            })}
            label={labels.bypassVatno}
            disabled={!isActive}
          />
        </>
      ) : null}

      {vatnoExist && commuter ? (
        <Banner className="col-span-full">{disclaimers.vatnoCommuter}</Banner>
      ) : null}

      <FormButton
        loadingLabel={labels.loading}
        namespace={namespace}
        onClick={validate}
        disabled={!isActive || !isValid}
      />
    </FieldSet>
  );
};
